/**
 * Client Downloads
 * Browse and download files
 */

import React, { useState, useEffect } from 'react';
import {
  Download,
  Folder,
  HardDrive,
  Calendar,
  TrendingUp,
  Clock,
  FileText,
} from 'lucide-react';

interface Category {
  id: number;
  name: string;
  description: string | null;
  file_count: number;
}

interface DownloadItem {
  id: number;
  category_id: number;
  name: string;
  description: string | null;
  filename: string;
  file_size: number;
  file_type: string;
  version: string | null;
  downloads_count: number;
  created_at: string;
}

export const Downloads: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [popularDownloads, setPopularDownloads] = useState<DownloadItem[]>([]);
  const [recentDownloads, setRecentDownloads] = useState<DownloadItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
    loadDownloads();
    loadPopularDownloads();
    loadRecentDownloads();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/v1/client/downloads/categories', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadDownloads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category_id', selectedCategory.toString());

      const response = await fetch(`/api/v1/client/downloads?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setDownloads(data.data.downloads);
      }
    } catch (error) {
      console.error('Failed to load downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPopularDownloads = async () => {
    try {
      const response = await fetch('/api/v1/client/downloads/popular?limit=5', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setPopularDownloads(data.data.downloads);
      }
    } catch (error) {
      console.error('Failed to load popular downloads:', error);
    }
  };

  const loadRecentDownloads = async () => {
    try {
      const response = await fetch('/api/v1/client/downloads/recent?limit=5', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setRecentDownloads(data.data.downloads);
      }
    } catch (error) {
      console.error('Failed to load recent downloads:', error);
    }
  };

  const handleDownload = async (downloadId: number, filename: string) => {
    try {
      const response = await fetch(`/api/v1/client/downloads/${downloadId}/download`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        // Open download URL in new tab
        window.open(data.data.url, '_blank');

        // Reload downloads to update counts
        loadDownloads();
        loadPopularDownloads();
      }
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('zip') || fileType.includes('compressed')) return '📦';
    return '📎';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Download className="w-6 h-6" />
          Downloads
        </h1>
        <p className="text-slate-400 mt-1">
          Access available files and resources
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Categories */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-lg font-medium text-white mb-4">Categories</h3>
            <div className="space-y-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === ''
                    ? 'bg-orange-500 text-white'
                    : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                All Files
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                    selectedCategory === category.id
                      ? 'bg-orange-500 text-white'
                      : 'text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    {category.name}
                  </span>
                  <span className="text-sm text-slate-400">
                    {category.file_count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Popular Downloads */}
          {popularDownloads.length > 0 && (
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-400" />
                Popular
              </h3>
              <div className="space-y-3">
                {popularDownloads.map((download) => (
                  <button
                    key={download.id}
                    onClick={() => handleDownload(download.id, download.filename)}
                    className="block w-full text-left p-3 bg-gray-750 hover:bg-slate-700/50 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{getFileIcon(download.file_type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium text-sm truncate">
                          {download.name}
                        </div>
                        <div className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                          <Download className="w-3 h-3" />
                          {download.downloads_count}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recent Downloads */}
          {recentDownloads.length > 0 && (
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                Recent
              </h3>
              <div className="space-y-3">
                {recentDownloads.map((download) => (
                  <button
                    key={download.id}
                    onClick={() => handleDownload(download.id, download.filename)}
                    className="block w-full text-left p-3 bg-gray-750 hover:bg-slate-700/50 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{getFileIcon(download.file_type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium text-sm truncate">
                          {download.name}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {new Date(download.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : downloads.length === 0 ? (
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No files available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {downloads.map((download) => {
                const category = categories.find((c) => c.id === download.category_id);

                return (
                  <div
                    key={download.id}
                    className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/30 transition-colors"
                  >
                    {/* File icon and name */}
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-4xl">{getFileIcon(download.file_type)}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
                          <span className="truncate">{download.name}</span>
                          {download.version && (
                            <span className="px-2 py-0.5 bg-blue-900 text-blue-300 rounded text-xs flex-shrink-0">
                              v{download.version}
                            </span>
                          )}
                        </h3>
                        {category && (
                          <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-xs">
                            {category.name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    {download.description && (
                      <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                        {download.description}
                      </p>
                    )}

                    {/* File info */}
                    <div className="flex items-center gap-4 mb-4 text-slate-400 text-sm">
                      <div className="flex items-center gap-1">
                        <HardDrive className="w-4 h-4" />
                        {formatFileSize(download.file_size)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        {download.downloads_count}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(download.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Download button */}
                    <button
                      onClick={() => handleDownload(download.id, download.filename)}
                      className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Downloads;
